import { I18nService } from '../services/i18n.service';
import { Location } from '../models/classes/location';
import { formatDistanceToNow } from 'date-fns';
import { REGEX_CONSTANT } from '../constants/regex.constant';
import convert, { Unit } from 'convert';
import { GLOBAL_CONSTANT } from '../constants/global.constant';

export function groupBy<T>(arr: T[], key: keyof T): T[][] {
  const grouped = arr.reduce(
    (acc, item) => {
      const groupKey = item[key] as unknown as string | number;
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    },
    {} as Record<string | number, T[]>
  );

  return Object.values(grouped);
}

export function sanitize(obj: any) {
  const blockedKeys = ['__proto__', 'constructor', 'prototype'];

  return Object.keys(obj).reduce((acc: any, key) => {
    if (!blockedKeys.includes(key)) {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
}

export function logError(err: unknown): void {
  if (err instanceof Error && err.stack) {
    console.error(err.stack);
  } else {
    console.error('Unknown error');
  }
}

export function escapeRegex(input: any) {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

// additional-info-popup

interface UnitMap {
  mt: string;
  im: string;
  md: string;
}

export function safeParse<T = any>(json: string | undefined): T | undefined {
  if (!json) return undefined;
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
}

export function isUnitMap(u: unknown): u is UnitMap {
  return (
    !!u && typeof u === 'object' && 'mt' in (u as any) && 'im' in (u as any) && 'md' in (u as any)
  );
}

export function normalizeUnit(unitInput: string | UnitMap | undefined): UnitMap {
  if (isUnitMap(unitInput)) return unitInput;
  if (typeof unitInput === 'string') {
    const parsed = safeParse<UnitMap>(unitInput);
    if (parsed && isUnitMap(parsed)) return parsed;
  }
  return { mt: '', im: '', md: '' };
}

export function getUnitPrefix(prefixJson: string | undefined, i18n: any) {
  const parsed = safeParse(prefixJson) ?? { mt: '', im: '' };
  const ms = i18n.measureMentSystem() as 'mt' | 'im';
  return parsed[ms] ?? '';
}

export function currentNumericValue(a: any): number | null {
  if (typeof a.value === 'number' && Number.isFinite(a.value)) return a.value;
  if (typeof a.valueInProgess === 'number' && Number.isFinite(a.valueInProgess))
    return a.valueInProgess;
  return null;
}

export function fieldInvalid(a: any): boolean {
  if (a.fieldType === 'radio') return false;

  const hasValue =
    (a.value != null && !Number.isNaN(a.value)) || // numeric
    (a.text && a.text.trim().length > 0) || // text or dropdown
    (a.multipleValues && a.multipleValues.some((v: any) => v.trim().length > 0)); // multiple

  const requiredMissing = !a.optional && !hasValue;

  const exceedsMax = a.maxValue != null && a.value != null && a.value > a.maxValue!;
  const belowMin = a.minValue != null && a.value != null && a.value < a.minValue!;

  return requiredMissing || exceedsMax || belowMin;
}

export function trackByIndex(index: number) {
  return index;
}

// rename-card-header
export function typeOfDeleteObj(obj: Location): string {
  switch (obj.curLayer) {
    case 'c':
      return 'plant';
    case 'd':
    case 'k':
      return obj.IsBuilding ? 'building' : 'area';
    case 'e':
      return 'floor';
    case 'f':
      return 'section';
    case 'l':
      return 'zone';
    case 'm':
      return 'scrossing';
    case 'g':
      return 'associatedeq';
    default:
      return '';
  }
}

// collapse-card-layer
export function getIconByEquipmentType(location: Location): string {
  // workaround until we get all the icons
  const type = location.equipmentType?.toLocaleLowerCase() ?? '';
  const normalized = type.replace(/\s/g, ''); // steam trap etc. may contain spaces
  if (
    type === 'flange' ||
    type === 'tank' ||
    type === 'valve' ||
    type === 'pipe' ||
    normalized === 'steamtrap' ||
    normalized === 'rotatingequipment'
  ) {
    return `icons:${normalized}-small-icon`;
  }
  return '';
}

// common-form
export function getName(value: any, i18n: I18nService) {
  return i18n.parse(JSON.parse(value));
}

// data-protection
export function initializeContent(): any[] {
  return [
    // 1. General Section
    { type: 'h3', key: 'general_title' },
    { type: 'paragraph', key: 'general_content', usKey: 'general_content_us' },
    { type: 'paragraph', key: 'general_content1' },
    { type: 'paragraph', key: 'general_content2' },

    // 2. Collection Section
    { type: 'h3', key: 'collection_title' },
    {
      type: 'paragraph',
      key: 'collection_content',
      usKey: 'collection_content_us',
    },
    // Generate collection_content1 to collection_content9
    ...(Array.from({ length: 9 }, (_, i) => ({
      type: 'paragraph',
      key: `collection_content${i + 1}`,
    })) as any[]),

    // 3. Further Processing Section
    { type: 'h3', key: 'further_processing_title' },
    { type: 'h4', key: 'app_use_title' },
    { type: 'paragraph', key: 'further_processing_content' },
    { type: 'paragraph', key: 'further_processing_content1' },

    // 4. Consent Section (Special structure)
    { type: 'h3', key: 'consent_title' },
    {
      type: 'special',
      key: 'consent_block',
      items: [
        {
          type: 'paragraph',
          key: 'consent_content_p0', // Placeholder key for the block
          class: 'complex-p',
        },
        { type: 'paragraph', key: 'consent_content1' },
        { type: 'paragraph', key: 'consent_content2' },
        { type: 'paragraph', key: 'further_processing_content1' },
        {
          type: 'paragraph',
          key: 'consent_content_p3', // Placeholder key for the block
          class: 'complex-p-spans',
        },
        { type: 'paragraph', key: 'consent_content6_1' },
        { type: 'paragraph', key: 'consent_content7' },
      ],
    },

    // 5. Cookies Section
    { type: 'h3', key: 'cookies_title' },
    { type: 'paragraph', key: 'cookies_content' },
    { type: 'paragraph', key: 'cookies_content1' },

    // 6. Rights Section (Special structure)
    { type: 'h3', key: 'rights_title' },
    { type: 'paragraph', key: 'rights_content' },
    // Generate rights_content1 to rights_content5 as list-items
    ...(Array.from({ length: 5 }, (_, i) => ({
      type: 'paragraph',
      key: `rights_content${i + 1}`,
      class: 'list-item',
    })) as any[]),

    // Right to Object Sub-Section (Part of Rights)
    { type: 'h4', key: 'right_to_object_title' },
    { type: 'paragraph', key: 'right_to_object_content', class: 'body-bold' },
    { type: 'paragraph', key: 'right_to_object_content1' },
    {
      type: 'paragraph',
      key: 'right_to_object_content2',
      usKey: 'right_to_object_content2_us',
    },

    // 7. Sharing Data Section
    { type: 'h3', key: 'sharing_data_title' },
    // Generate sharing_data_content to sharing_data_content5
    ...(Array.from({ length: 6 }, (_, i) => ({
      type: 'paragraph',
      key: `sharing_data_content${i > 0 ? i : ''}`, // content, content1..content5
    })) as any[]),
  ];
}

// Filter
export function unique(value: string, index: number, array: string[]) {
  return index === array.findIndex((item: string) => item === value);
}

export function sortAreas(areas: any, i18n: I18nService) {
  return areas.sort((a: any, b: any) => {
    if (i18n.parse(a.objectName).toUpperCase() > i18n.parse(b.objectName).toUpperCase()) return 1;
    if (i18n.parse(a.objectName).toUpperCase() < i18n.parse(b.objectName).toUpperCase()) return -1;
    return 0;
  });
}

export function fnLocationCompare(o1: Location, o2: Location) {
  if (!o1 || !o2) return false;
  return o1.objectID === o2.objectID;
}

export function fnCustomerCompare(o1: string, o2: string) {
  if (!o1 || !o2) return false;
  return o1 === o2;
}

export function getChildrenLayerNames(locs: Location[], i18n: I18nService): string {
  if (!locs || locs.length === 0) {
    return 'N/A';
  }

  return locs
    .map((item: Location) => i18n.parse(item.layerName))
    .filter(unique)
    .filter((item) => !!item)
    .reduce((a: string, b: string) => a + (a ? '/' : '') + b, '');
}

// image-crop
export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function processImage(base64: string | null | undefined) {
  if (!base64) return null;
  const resized = await reduceImageFileSize(base64);
  return resized;
}

export async function reduceImageFileSize(base64Str: string, MAX_WIDTH = 450, MAX_HEIGHT = 450) {
  return new Promise<string>((resolve) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      let width = img.width;
      let height = img.height;

      if (width > height && width > MAX_WIDTH) {
        height *= MAX_WIDTH / width;
        width = MAX_WIDTH;
      } else if (height > MAX_HEIGHT) {
        width *= MAX_HEIGHT / height;
        height = MAX_HEIGHT;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL());
    };
  });
}

// sensor-input
export function calculateSize(value: string): number {
  return Math.max(1, ('' + value).length);
}

// terms-of-use
export function generateKeys(prefix: string, start: number, end: number): string[] {
  return Array.from({ length: end - start + 1 }, (_, i) => `${prefix}_${i + start}_content`);
}
// Utility to map default keys to LATAM keys (e.g., 'scope_1_1_content' -> 'terms_scope_1_1_content_latam')
export function mapToLatam(
  keys: string[],
  titlePrefix = 'terms_',
  contentSuffix = '_latam'
): string[] {
  return keys.map((key) => {
    // For main content, extract parts and rebuild with prefix/suffix
    const parts = key.split('_');
    if (key.includes('content')) {
      const scopeNum = parts[1];
      const contentNum = parts[2];
      const subLetter = parts[3] ? `_${parts[3]}` : ''; // Check for 'a', 'b', etc.
      return `${titlePrefix}scope_${scopeNum}_${contentNum}_content${subLetter}${contentSuffix}`;
    }
    return key;
  });
}

// tile
export function minutesToNow(timestamp: Date | number): string {
  return formatDistanceToNow(timestamp);
}

// For input's oninput event

/**
 * Sanitizes numeric input where a decimal is allowed and ensures at most one '.'.
 * - Removes all non [0-9 .] characters
 * - Collapses extra dots to keep only the first dot
 */
export function sanitizeDecimalOnce(value: string): string {
  if (!value) return '';
  // Keep digits and dots only
  let sanitized = value.replace(REGEX_CONSTANT.NON_DECIMAL_CHARS, '');
  // Enforce a single dot
  sanitized = sanitized.replace(REGEX_CONSTANT.MULTIPLE_DOTS, '$1');
  return sanitized;
}

/**
 * Sanitizes general Unicode text: allows letters, numbers, punctuation, separators (spaces), and newlines.
 * Removes everything else.
 */
export function sanitizeUnicodeText(value: string): string {
  if (!value) return '';
  return value.replace(REGEX_CONSTANT.NON_UNICODE_TEXT_CHARS, '');
}

export function convertUnitToImperial(unit: string): string {
  if (!unit) return unit;
  const normalized = unit.toLowerCase().replace(' ', '');
  const map: Record<string, string> = {
    mm: 'in',
    cm: 'in',
    dm: 'in',
    m: 'ft',
    km: 'ml',
    'mm²': 'in²',
    'cm²': 'in²',
    'dm²': 'in²',
    'm²': 'ft²',
    a: 'ft²',
    ha: 'ft²',
    'km²': 'ml²',
    'mm³': 'in³',
    'cm³': 'in³',
    'dm³': 'in³',
    l: 'ft³',
    'm³': 'ft³',
    'km³': 'ml³',
    g: 'oz',
    kg: 'lb',
    '°c': '°F',
  };
  return map[normalized] || unit;
}

export function convertUnitToMetric(unit: string): string {
  if (!unit) return unit;
  const normalized = unit.toLowerCase().replace(' ', '');
  const map: Record<string, string> = {
    in: 'cm',
    ft: 'm',
    ml: 'km',
    'in²': 'cm²',
    'ft²': 'm²',
    'ml²': 'km²',
    'in³': 'cm³',
    'ft³': 'm³',
    'ml³': 'km³',
    oz: 'g',
    lb: 'kg',
    '°f': '°C',
  };
  return map[normalized] || unit;
}

function determineMeasurementType(unit: string): string {
  const lengthUnits = ['mm', 'cm', 'dm', 'm', 'km', 'in', 'ft', 'ml'];
  const areaUnits = ['mm²', 'cm²', 'dm²', 'm²', 'a', 'ha', 'km²', 'in²', 'ft²', 'ml²'];
  const volumeUnits = ['mm³', 'cm³', 'dm³', 'l', 'm³', 'km³', 'in³', 'ft³', 'ml³'];
  const massUnits = ['g', 'kg', 'oz', 'lb'];
  const temperatureUnits = ['°c', '°f'];

  const normalized = unit.toLowerCase().replace(' ', '');
  if (lengthUnits.includes(normalized)) return 'length';
  if (areaUnits.includes(normalized)) return 'area';
  if (volumeUnits.includes(normalized)) return 'volume';
  if (massUnits.includes(normalized)) return 'mass';
  if (temperatureUnits.includes(normalized)) return 'temperature';
  return 'unknown';
}

export function convertValue(value: number, oldUnit: string, newUnit: string): number {
  const measurementType = determineMeasurementType(oldUnit);
  switch (measurementType) {
    case 'length':
    case 'area':
    case 'volume':
    case 'mass':
    case 'temperature': {
      const fromUnit = oldUnit.replace('°', '').toLowerCase() as Unit;
      const toUnit = newUnit.replace('°', '').toLowerCase() as Unit;
      try {
        return Math.round(convert(value, fromUnit).to(toUnit) * 10) / 10;
      } catch {
        return value;
      }
    }
    default:
      return value;
  }
}

export function isValidDate(d: any): boolean {
  return d instanceof Date && !isNaN(+d);
}

function isValidStringInput(input: unknown): input is string {
  return typeof input === 'string' && input.trim().length > 0;
}

function handleFormattingError(
  functionName: string,
  error: unknown,
  input: string,
  fallback: string
): string {
  console.error('Error occurred', { functionName, error, input });
  return fallback;
}

export function formatSerialNumber(input: unknown): string {
  if (!isValidStringInput(input)) {
    return '';
  }

  const normalizedInput = String(input).trim();

  try {
    return normalizedInput.toUpperCase().replace(GLOBAL_CONSTANT.ALPHANUMERIC_REGEX, '');
  } catch (error) {
    return handleFormattingError('formatSerialNumber', error, normalizedInput, normalizedInput);
  }
}

export function formatMAC(input: unknown): string {
  if (!isValidStringInput(input)) {
    return '';
  }

  const normalizedInput = String(input).trim();

  try {
    // Return original input if length is not exactly 12 characters
    if (normalizedInput.length !== GLOBAL_CONSTANT.MAC_ADDRESS_LENGTH) {
      return normalizedInput;
    }

    const uppercaseInput = normalizedInput.toUpperCase();
    const matches = uppercaseInput.match(GLOBAL_CONSTANT.MAC_ADDRESS_REGEX);

    return matches ? matches.join(GLOBAL_CONSTANT.COLON) : normalizedInput;
  } catch (error) {
    return handleFormattingError('formatMAC', error, normalizedInput, normalizedInput);
  }
}

export function generateUniqueIdentifier() {
  return crypto.randomUUID();
}

export function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function secureRandom(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32;
}
